import { useQuery } from '@tanstack/react-query'

export interface CoffeeShop {
  id: string
  name: string
  lat: number
  lon: number
  city?: string
  brand?: string
  cuisine?: string
  opening_hours?: string
  phone?: string
  website?: string
  wifi?: string
  outdoor_seating?: string
  wheelchair?: string
  takeaway?: string
  delivery?: string
  rating?: string
  stars?: string
  properties: Record<string, any>
}

export interface Bounds {
  south: number
  west: number
  north: number
  east: number
}

export interface CoffeeShopResponse {
  type: 'FeatureCollection'
  features: Array<{
    type: 'Feature'
    id: string
    geometry: {
      type: 'Point'
      coordinates: [number, number] // [lng, lat]
    }
    properties: Record<string, any>
  }>
  bbox?: [number, number, number, number]
  count: number
  total_available: number
}

export interface CoffeeShopStats {
  datasets: Record<string, {
    total_coffee_shops: number
    cities: number
    city_breakdown: Record<string, number>
    top_brands: Array<[string, number]>
    amenities: {
      wifi: number
      outdoor_seating: number
      wheelchair: number
      takeaway: number
      delivery: number
    }
  }>
  total_datasets: number
  global_stats: {
    total_coffee_shops: number
    total_cities: number
  }
}

// Type alias for feature to improve type safety
type CoffeeShopFeature = CoffeeShopResponse['features'][0]

// Configuration - dynamically determine API base URL
function getApiBaseUrl(): string {
  // If environment variable is set, use it
  if (import.meta.env.VITE_DATA_API_URL) {
    return import.meta.env.VITE_DATA_API_URL
  }

  // Otherwise, determine dynamically based on current location
  if (typeof window !== 'undefined') {
    const { protocol, hostname, port } = window.location

    // If we're on localhost (development), use the dev server
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return 'http://localhost:3001'
    }

    // For production deployments, use the same origin with /api prefix
    // This assumes the production server serves both frontend and API
    const baseUrl = port && port !== '80' && port !== '443'
      ? `${protocol}//${hostname}:${port}`
      : `${protocol}//${hostname}`

    return `${baseUrl}/api`
  }

  // Fallback for SSR or other edge cases
  return 'http://localhost:3001'
}

const DATA_API_BASE = getApiBaseUrl()
const API_KEY = import.meta.env.VITE_API_KEY || 'dev-key-change-in-production'

// Debug logging (only in development)
if (import.meta.env.DEV) {
  console.log('API Configuration:', {
    DATA_API_BASE,
    currentLocation: typeof window !== 'undefined' ? window.location.href : 'SSR',
    envVar: import.meta.env.VITE_DATA_API_URL || 'not set'
  })
}

// Reusable mapping function to eliminate duplication
function mapFeatureToCoffeeShop(feature: CoffeeShopFeature): CoffeeShop {
  return {
    id: feature.id,
    name: feature.properties.name || 'Unknown Coffee Shop',
    lat: feature.geometry.coordinates[1], // GeoJSON uses [lng, lat]
    lon: feature.geometry.coordinates[0],
    city: feature.properties.city,
    brand: feature.properties.brand,
    cuisine: feature.properties.cuisine,
    opening_hours: feature.properties.opening_hours,
    phone: feature.properties.phone,
    website: feature.properties.website,
    wifi: feature.properties.wifi,
    outdoor_seating: feature.properties.outdoor_seating,
    wheelchair: feature.properties.wheelchair,
    takeaway: feature.properties.takeaway,
    delivery: feature.properties.delivery,
    rating: feature.properties.rating,
    stars: feature.properties.stars,
    properties: feature.properties
  }
}

async function fetchCoffeeShops(bounds: Bounds): Promise<CoffeeShop[]> {
  const bbox = `${bounds.west},${bounds.south},${bounds.east},${bounds.north}`
  const url = `${DATA_API_BASE}/coffee-shops/bbox/${bbox}?limit=2000`

  if (import.meta.env.DEV) {
    console.log('Fetching coffee shops:', { bounds, url })
  }

  try {
    const response = await fetch(url, {
      headers: {
        'X-API-Key': API_KEY,
        'Content-Type': 'application/json'
      },
      // Add timeout to prevent hanging requests
      signal: AbortSignal.timeout(30000) // 30 second timeout
    })

    if (!response.ok) {
      const errorText = await response.text()
      let errorData
      try {
        errorData = JSON.parse(errorText)
      } catch {
        errorData = { error: 'Unknown error', message: errorText }
      }

      // Handle specific error cases
      if (response.status === 404 && errorData.error === 'Dataset not found') {
        console.warn('Dataset not found, attempting cache reload...')
        // Try to trigger cache reload
        try {
          await fetch(`${DATA_API_BASE}/cache/reload/combined`, {
            method: 'POST',
            headers: {
              'X-API-Key': API_KEY,
              'Content-Type': 'application/json'
            }
          })
          // Wait a moment for reload to complete
          await new Promise(resolve => setTimeout(resolve, 2000))
          // Retry the original request once
          const retryResponse = await fetch(url, {
            headers: {
              'X-API-Key': API_KEY,
              'Content-Type': 'application/json'
            },
            signal: AbortSignal.timeout(30000)
          })
          if (retryResponse.ok) {
            const retryData: CoffeeShopResponse = await retryResponse.json()
            return retryData.features.map(mapFeatureToCoffeeShop)
          }
        } catch (reloadError) {
          console.error('Cache reload failed:', reloadError)
        }
      }

      throw new Error(`API Error ${response.status}: ${errorData.message || errorData.error || 'Unknown error'}`)
    }

    const data: CoffeeShopResponse = await response.json()

    // Validate response structure
    if (!data.features || !Array.isArray(data.features)) {
      throw new Error('Invalid API response: missing or invalid features array')
    }

    if (import.meta.env.DEV) {
      console.log('Coffee shops API response:', {
        count: data.count,
        total_available: data.total_available,
        features_length: data.features.length
      })
    }

    return data.features.map(mapFeatureToCoffeeShop)
  } catch (error) {
    if (import.meta.env.DEV) {
      console.error('Error fetching coffee shops:', error)
    }

    // Enhance error message for better debugging
    if (error instanceof Error) {
      if (error.name === 'AbortError') {
        throw new Error('Request timeout: The server took too long to respond')
      } else if (error.message.includes('Failed to fetch')) {
        throw new Error('Network error: Unable to connect to the server')
      }
    }

    throw error
  }
}

async function fetchAllCoffeeShops(): Promise<CoffeeShop[]> {
  const url = `${DATA_API_BASE}/coffee-shops?limit=10000`
  
  try {
    const response = await fetch(url, {
      headers: {
        'X-API-Key': API_KEY,
        'Content-Type': 'application/json'
      }
    })
    if (!response.ok) {
      throw new Error(`Failed to fetch all coffee shops: ${response.status}`)
    }
    
    const data: CoffeeShopResponse = await response.json()
    
    return data.features.map(mapFeatureToCoffeeShop)
  } catch (error) {
    if (import.meta.env.DEV) {
      console.error('Error fetching all coffee shops:', error)
    }
    throw error
  }
}

export function useCoffeeShops(bounds: Bounds | null) {
  return useQuery<CoffeeShop[], Error>({
    queryKey: ['coffeeShops', bounds],
    queryFn: () => bounds ? fetchCoffeeShops(bounds) : Promise.resolve([]),
    enabled: !!bounds,
    staleTime: 5 * 60 * 1000, // 5 minutes - reduced for fresher data
    gcTime: 15 * 60 * 1000, // 15 minutes - reduced to free memory sooner
    retry: (failureCount, error) => {
      // Don't retry on certain errors
      if (error instanceof Error) {
        if (error.message.includes('timeout') ||
            error.message.includes('AbortError') ||
            error.message.includes('Network error')) {
          return failureCount < 2 // Only retry twice for network issues
        }
        if (error.message.includes('Dataset not found')) {
          return failureCount < 1 // Only retry once for dataset issues (we handle reload internally)
        }
      }
      return failureCount < 3 // Default retry count
    },
    retryDelay: (attemptIndex, error) => {
      // Longer delays for dataset issues to allow cache reload
      if (error instanceof Error && error.message.includes('Dataset not found')) {
        return Math.min(5000 * 2 ** attemptIndex, 15000) // 5s, 10s, 15s
      }
      return Math.min(1000 * 2 ** attemptIndex, 5000) // Standard exponential backoff
    },
    // Prevent background refetching during user interaction
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
    // Network mode to handle offline scenarios
    networkMode: 'online'
  })
}

export function useAllCoffeeShops() {
  return useQuery<CoffeeShop[], Error>({
    queryKey: ['allCoffeeShops'],
    queryFn: fetchAllCoffeeShops,
    staleTime: 30 * 60 * 1000, // 30 minutes
    gcTime: 60 * 60 * 1000, // 1 hour
    retry: 2,
    retryDelay: 1000,
  })
}

export function useCoffeeShopStats() {
  return useQuery<CoffeeShopStats, Error>({
    queryKey: ['coffeeShopStats'],
    queryFn: async () => {
      const response = await fetch(`${DATA_API_BASE}/stats`, {
        headers: {
          'X-API-Key': API_KEY,
          'Content-Type': 'application/json'
        }
      })
      if (!response.ok) {
        throw new Error(`Failed to fetch stats: ${response.status}`)
      }
      const data: CoffeeShopStats = await response.json()
      return data
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 15 * 60 * 1000, // 15 minutes
  })
} 